import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { forEach } from 'lodash';
import SiteNavBDesktopLevelA from 'components/SiteNav/SiteNavB/SiteNavBDesktop/SiteNavBDesktopLevelA';
import SiteNavBDesktopLevelB from 'components/SiteNav/SiteNavB/SiteNavBDesktop/SiteNavBDesktopLevelB';
import SiteNavBDesktopLevelC from 'components/SiteNav/SiteNavB/SiteNavBDesktop/SiteNavBDesktopLevelC';
import classes from './SiteNavBDesktop.module.scss';

const SiteNavBDesktop = (props) => {
  const {
    items = [],
    levelBItems = [],
    ...rest
  } = props;

  const [levelBSelectedIndex, setLevelBSelectedIndex] = useState(-1);
  const [levelBPrevIndex, setLevelBPrevIndex] = useState(-1);

  const onLevelBItemClick = (navItemIndex) => {
    setLevelBPrevIndex(levelBSelectedIndex);
    setLevelBSelectedIndex(levelBSelectedIndex === navItemIndex ? -1 : navItemIndex);
  };

  const itemsPerColumn = 11;

  // targetItemsPerColumn is the number of items in a column we are shooting for.
  // It may be more based on where the split in the column occurrs because we don't
  // want to split child items (level e items).
  const createSections = (cItems, targetItemsPerColumn) => {
    let maxItemsPerColumn = 0;
    let levelCSections = [];

    levelCSections = cItems.map((c) => {
      const levelCColumns = [];
      let currentColumn = [];
      let itemsInColumn = 0;

      const {
        heading: cHeading,
        href: cHref,
        target: cTarget,
        items: levelDItems = [],
      } = c;

      forEach(levelDItems, (d) => {
        if (itemsInColumn >= targetItemsPerColumn) {
          // If the above statement is true then split to a new column.
          levelCColumns.push(currentColumn);
          currentColumn = [];
          itemsInColumn = 0;
        }

        currentColumn.push(d);

        const {
          items: levelEItems = [],
        } = d;

        // Level D and Level E items are included in the total number of items in a column.
        itemsInColumn += levelEItems.length + 1;

        // Set the highest number of items in a column.
        maxItemsPerColumn = itemsInColumn > maxItemsPerColumn ? itemsInColumn : maxItemsPerColumn;
      });

      // Push the remaining items into the current column and move to the next section of the nav.
      // Reset everything.
      levelCColumns.push(currentColumn);
      currentColumn = [];
      itemsInColumn = 0;

      return {
        heading: cHeading,
        href: cHref,
        target: cTarget,
        columns: levelCColumns,
      };
    });

    // If any of the columns have more than the targetItemsPerColumn
    // then rebuild the sections so that all columns will have the same number of items.
    if (maxItemsPerColumn > targetItemsPerColumn) {
      levelCSections = createSections(cItems, maxItemsPerColumn);
    }

    return levelCSections;
  };

  const navItemList = useRef(null);
  const slideDowns = useRef(levelBItems.map(() => React.createRef()));
  useEffect(() => {
    function handleClickOutside(event) {
      if (slideDowns.current && slideDowns.current[levelBSelectedIndex]
        && slideDowns.current[levelBSelectedIndex].current
        && !slideDowns.current[levelBSelectedIndex].current.contains(event.target)
        && navItemList.current && !navItemList.current.contains(event.target)) {
        setLevelBSelectedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [levelBSelectedIndex]);

  return (
    <div className={classes.container}>
      <SiteNavBDesktopLevelA items={items} />
      <SiteNavBDesktopLevelB
        ref={navItemList}
        items={levelBItems}
        levelBSelectedIndex={levelBSelectedIndex}
        onNavItemClick={onLevelBItemClick}
        {...rest}
      />
      {levelBItems.map((i, idx) => {
        // Only animate when either levelB index is -1
        // AND the other levelB index === component index
        const animate = (levelBSelectedIndex === -1 && levelBPrevIndex === idx)
          || (levelBSelectedIndex === idx && levelBPrevIndex === -1);

        return (
          <SiteNavBDesktopLevelC
            key={`levelC-item-${idx}`}
            ref={slideDowns.current[idx]}
            show={levelBSelectedIndex === idx}
            animate={animate}
            heading={i.heading}
            href={i.href}
            target={i.target}
            sections={createSections(i.items, itemsPerColumn)}
            onAnimationEnd={() => {}}
          />
        );
      })}
    </div>
  );
};

const TItem = PropTypes.shape({
  heading: PropTypes.string,
  href: PropTypes.string,
  target: PropTypes.string,
  items: PropTypes.array,
});

SiteNavBDesktop.propTypes = {
  items: PropTypes.arrayOf(TItem),
  levelBItems: PropTypes.arrayOf(TItem),
};

export default SiteNavBDesktop;
